/**
 * AudinexaPipelineDrawer — full 13-state service-job UI.
 *
 * Opens when the user clicks a row in ServiceTicketsPage.
 * Provides:
 *   • Visual pipeline timeline with stamped-at dates
 *   • State transition buttons (only shows legal next states)
 *   • Book Courier form (OUTBOUND + INBOUND)
 *   • Record Estimate form (auto-creates pending CustomerApproval)
 *   • Approve / Reject CTA for front-desk
 *   • Job Card PDF download
 *   • "Send WhatsApp" link using the per-status template
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConflictResolutionModal from '../../components/ConflictResolutionModal';
import ErrorToast, { describeError } from '../../components/ErrorToast';
import ReportViewerModal from '../../components/ReportViewerModal';
import { ServiceTicketActions } from '../ha/ServiceTicketPhase14Actions';
import ServiceSlipPrintModal from '../ha/ServiceSlipPrintModal';
import ShippingLabelModal from '../../components/ShippingLabelModal';
import WhatsAppButton from '../../components/WhatsAppButton';
import EmailDocButton from '../../components/EmailDocButton';
import { useAuth } from '../../AuthContext';

import useEscapeKey from '../../hooks/useEscapeKey';
import { VendorReportStep } from './VendorReportStep';
import InboundReturnStep from './InboundReturnStep';
import SerialReassignStep from './SerialReassignStep';

const VENDOR_STAGES = ['DELIVERED_TO_COMPANY', 'ESTIMATE_PENDING', 'CLIENT_APPROVED', 'CLIENT_REJECTED', 'REPAIR_IN_PROGRESS',
  'RETURN_SHIPPED', 'READY_FOR_PICKUP', 'DELIVERED_TO_CLIENT', 'NOT_PROCEEDING', 'CLOSED'];
const REASSIGN_ROLES = ['clinic_owner', 'clinic_admin', 'front_desk', 'audiologist', 'super_admin'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const PIPELINE_ORDER = [
  'RECEIVED', 'INSPECTED', 'AWAITING_DISPATCH', 'DISPATCHED', 'IN_TRANSIT',
  'DELIVERED_TO_COMPANY', 'ESTIMATE_PENDING', 'CLIENT_APPROVED',
  'REPAIR_IN_PROGRESS', 'RETURN_SHIPPED', 'READY_FOR_PICKUP',
  'DELIVERED_TO_CLIENT', 'CLOSED',
];
const PIPELINE_LABELS = {
  RECEIVED: 'Received', INSPECTED: 'Inspected', AWAITING_DISPATCH: 'Awaiting Dispatch',
  DISPATCHED: 'Dispatched', IN_TRANSIT: 'In Transit',
  DELIVERED_TO_COMPANY: 'At Service Centre', ESTIMATE_PENDING: 'Estimate Pending',
  CLIENT_APPROVED: 'Client Approved', CLIENT_REJECTED: 'Client Rejected',
  REPAIR_IN_PROGRESS: 'Repair in Progress', RETURN_SHIPPED: 'Return Shipped',
  READY_FOR_PICKUP: 'Ready for Pickup', DELIVERED_TO_CLIENT: 'Delivered',
  CLOSED: 'Closed', CANCELLED: 'Cancelled', NOT_PROCEEDING: 'Closed – Not Proceeding',
};

// Legal next states mirror backend JOB_TRANSITIONS. Duplicated here so the UI
// doesn't have to round-trip to discover legal actions.
const NEXT_STATES = {
  RECEIVED:             ['INSPECTED', 'AWAITING_DISPATCH', 'READY_FOR_PICKUP', 'CANCELLED'],
  INSPECTED:            ['AWAITING_DISPATCH', 'READY_FOR_PICKUP', 'CANCELLED'],
  AWAITING_DISPATCH:    ['DISPATCHED', 'CANCELLED'],
  DISPATCHED:           ['IN_TRANSIT', 'CANCELLED'],
  IN_TRANSIT:           ['DELIVERED_TO_COMPANY', 'CANCELLED'],
  DELIVERED_TO_COMPANY: ['ESTIMATE_PENDING', 'REPAIR_IN_PROGRESS', 'CANCELLED'],
  ESTIMATE_PENDING:     ['CLIENT_APPROVED', 'NOT_PROCEEDING', 'CANCELLED'],
  CLIENT_APPROVED:      ['REPAIR_IN_PROGRESS', 'CANCELLED'],
  CLIENT_REJECTED:      ['RETURN_SHIPPED', 'NOT_PROCEEDING', 'CANCELLED'],
  REPAIR_IN_PROGRESS:   ['RETURN_SHIPPED', 'CANCELLED'],
  RETURN_SHIPPED:       ['READY_FOR_PICKUP', 'CANCELLED'],
  READY_FOR_PICKUP:     ['DELIVERED_TO_CLIENT', 'CANCELLED'],
  DELIVERED_TO_CLIENT:  ['CLOSED'],
  CLOSED:               [],
  CANCELLED:            [],
  NOT_PROCEEDING:       [],
};

export default function AudinexaPipelineDrawer({ ticketNo, onClose, onChanged }) {
  useEscapeKey(onClose);
  const [pipe, setPipe] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [waMessage, setWaMessage] = useState(null);
  // Conflict-resolution state (Phase 14: 3-way merge)
  const [conflict, setConflict] = useState(null);

  // Sub-forms
  const [showCourier, setShowCourier] = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  const [stampingShipment, setStampingShipment] = useState(null);
  const [jobCardOpen, setJobCardOpen] = useState(false);
  // Feb 2026 · A5 printable slip (drop-off OR completion). null = closed.
  const [slipMode, setSlipMode] = useState(null);
  // Feb 2026 · Shipping label (A5 landscape) for vendor-bound jobs.
  // We lazy-fetch the vendor + branch records only when the user clicks
  // the Print Shipping Label button — avoids an extra round-trip on every
  // drawer open for IN_CLINIC tickets.
  const [shipLabelOpen, setShipLabelOpen] = useState(false);
  const [shipVendor, setShipVendor] = useState(null);
  const [shipBranch, setShipBranch] = useState(null);
  const [shipClinicName, setShipClinicName] = useState('');
  const [shipLoading, setShipLoading] = useState(false);
  // 2026-02-19 · Clinic info for wa.me message templating
  const _authCtx = useAuth() || {};
  const _clinicCtx = _authCtx.clinic || {};

  const openShippingLabel = useCallback(async () => {
    const t2 = pipe?.ticket;
    if (!t2) return;
    setShipLoading(true);
    try {
      const [vRes, bRes] = await Promise.all([
        t2.vendor_id ? axios.get(`${API}/vendors/${t2.vendor_id}`).then(r => r.data).catch(() => null) : Promise.resolve(null),
        axios.get(`${API}/branches`).then(r => r.data).catch(() => []),
      ]);
      setShipVendor(vRes);
      const bMatch = (bRes || []).find(b => b.branch_id === t2.branch_id) || null;
      setShipBranch(bMatch);
      setShipClinicName(bMatch?.clinic_name || '');
      setShipLabelOpen(true);
    } finally {
      setShipLoading(false);
    }
  }, [pipe]);

  const load = useCallback(async () => {
    setErr(''); setLoading(true);
    try {
      const r = await axios.get(`${API}/ha/service-jobs/${ticketNo}/pipeline`);
      setPipe(r.data);
    } catch (e) { setErr(describeError(e, 'Failed to load pipeline')); }
    finally { setLoading(false); }
  }, [ticketNo]);

  useEffect(() => { load(); }, [load]);

  const curStatus = pipe?.normalised_status;
  const legalNext = useMemo(() => NEXT_STATES[curStatus] || [], [curStatus]);

  const transition = async (to_status, opts = {}) => {
    if (!opts.skipConfirm) {
      if (!window.confirm(`Move Job ${ticketNo} → ${PIPELINE_LABELS[to_status] || to_status}?`)) return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/ha/service-tickets/${ticketNo}/transition`, {
        to_status,
        note: opts.note || undefined,
        expected_version: pipe?.ticket?.version,
      });
      await load();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Server returned a 3-way merge payload — open the resolution modal
      if (detail && typeof detail === 'object' && detail.code === 'VERSION_MISMATCH') {
        setConflict({
          baseDoc: pipe.ticket,
          serverDoc: detail.current,
          localEdit: { ...pipe.ticket, status: to_status },
          currentVersion: detail.current_version,
          expectedVersion: detail.expected_version,
          intent: { kind: 'transition', to_status, note: opts.note },
        });
      } else {
        setErr(describeError(e, 'Transition failed'));
      }
    } finally { setBusy(false); }
  };

  // Apply the merged result from the conflict modal back via PUT, pinned to current_version.
  const resolveConflict = async (merged, newExpectedVersion) => {
    const intent = conflict?.intent || {};
    if (intent.kind === 'transition') {
      // The current SERVER status may differ from what user wanted to move to.
      // After the merge we re-issue the transition pinned to the new version.
      await axios.post(`${API}/ha/service-tickets/${ticketNo}/transition`, {
        to_status: intent.to_status,
        note: intent.note || undefined,
        expected_version: newExpectedVersion,
      });
    } else {
      await axios.put(`${API}/ha/service-tickets/${ticketNo}`, {
        ...merged,
        expected_version: newExpectedVersion,
      });
    }
    await load();
    onChanged && onChanged();
  };

  const loadWhatsApp = async (forceStatus) => {
    try {
      const r = await axios.get(`${API}/ha/service-tickets/${ticketNo}/whatsapp`, {
        params: forceStatus ? { status: forceStatus } : {},
      });
      setWaMessage(r.data);
    } catch (e) { setErr(describeError(e, 'WhatsApp render failed')); }
  };

  if (loading && !pipe) return <DrawerShell onClose={onClose}><Skel /></DrawerShell>;
  if (!pipe) return <DrawerShell onClose={onClose}><div className="p-6 text-rose-700 text-sm">{err || 'No data'}</div></DrawerShell>;

  const t = pipe.ticket;
  const stampedAt = {
    RECEIVED: t.created_at, INSPECTED: null, AWAITING_DISPATCH: null,
    DISPATCHED: t.dispatched_at, IN_TRANSIT: null,
    DELIVERED_TO_COMPANY: t.delivered_to_company_at,
    ESTIMATE_PENDING: t.estimate_received_at,
    CLIENT_APPROVED: t.client_decided_at, CLIENT_REJECTED: t.client_decided_at,
    REPAIR_IN_PROGRESS: null, RETURN_SHIPPED: t.return_shipped_at,
    READY_FOR_PICKUP: t.ready_at, DELIVERED_TO_CLIENT: t.delivered_to_client_at,
    CLOSED: t.closed_at,
  };
  const curIdx = PIPELINE_ORDER.indexOf(curStatus);

  return (
    <DrawerShell onClose={onClose} title={`Service Job · ${ticketNo}`}>
      {err && <ErrorToast err={err} testid="audinexa-pipeline-err" />}

      {/* ===== HEADER ===== */}
      <div className="flex items-start justify-between mb-5 pb-4 border-b border-slate-200">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Patient · Device</div>
          <div className="font-bold text-slate-800 mt-0.5">
            {t.patient_name} <span className="text-slate-400 font-normal">· {t.patient_mobile || '—'}</span>
          </div>
          <div className="text-xs text-slate-500 font-mono">{t.serial_no || t.serial_id || 'No device linked'}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setSlipMode('dropoff')}
                  data-testid="audinexa-dropoff-slip-btn"
                  className="px-3 py-1.5 text-xs font-bold border border-indigo-400 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 rounded"
                  title="Print the A5 acknowledgement slip given to the patient at drop-off">
            🖨️ Drop-off Slip
          </button>
          {/* Shipping Label — only when ticket is vendor-bound AND has reached
              the AWAITING_DISPATCH stage. Hidden on IN_CLINIC repairs. */}
          {t.repair_location === 'VENDOR' && t.vendor_id && curIdx >= PIPELINE_ORDER.indexOf('AWAITING_DISPATCH') && (
            <button onClick={openShippingLabel}
                    disabled={shipLoading}
                    data-testid="audinexa-shipping-label-btn"
                    className="px-3 py-1.5 text-xs font-bold border border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded disabled:opacity-50"
                    title="Print an A5 shipping label (FROM branch → TO vendor) for the courier package">
              {shipLoading ? 'Loading…' : '🖨️ Shipping Label'}
            </button>
          )}
          <button onClick={() => setJobCardOpen(true)}
                  data-testid="audinexa-job-card-pdf"
                  className="px-3 py-1.5 text-xs font-bold border border-slate-300 rounded hover:bg-slate-100"
                  title="Internal reference — full A4 job card PDF">
            📄 Job Card PDF
          </button>
          {/* 2026-02-19 · Per-spec wa.me click-to-chat (no API cost).
              Sits beside the existing template-based "WhatsApp" button
              which uses the server's per-status template; this one is
              the lightweight open-in-WhatsApp-Web flow. */}
          <WhatsAppButton
            mobile={t.patient_mobile}
            size="md"
            testid="audinexa-wa-patient-btn"
            label="WA Patient"
            message={(() => {
              const cName = _clinicCtx?.name || 'AUDINEXA Clinic';
              const cPhone = _clinicCtx?.phone || '';
              const statusLabel = PIPELINE_LABELS[curStatus] || curStatus || '—';
              const name = t.patient_name || 'Patient';
              const job = t.ticket_no || ticketNo;
              return `Dear ${name}, your hearing aid service job ${job} status: ${statusLabel}. ${cName}${cPhone ? ' — ' + cPhone : ''}`;
            })()}
          />
          <button onClick={() => loadWhatsApp()}
                  data-testid="audinexa-whatsapp-btn"
                  className="px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded">
            WhatsApp
          </button>
        </div>
      </div>

      {/* ===== TIMELINE ===== */}
      <div className="mb-6" data-testid="audinexa-pipeline-timeline">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Pipeline</div>
        <div className="flex overflow-x-auto gap-1 pb-2">
          {PIPELINE_ORDER.map((st, idx) => {
            const done = idx <= curIdx && curIdx >= 0;
            const active = idx === curIdx;
            const stamp = stampedAt[st];
            return (
              <div key={st}
                   data-testid={`audinexa-pipeline-step-${st}`}
                   className={`flex-shrink-0 min-w-[95px] rounded p-2 text-center border ${
                     active ? 'bg-indigo-600 text-white border-indigo-700 font-bold' :
                     done   ? 'bg-emerald-50 text-emerald-800 border-emerald-300' :
                              'bg-slate-50 text-slate-400 border-slate-200'
                   }`}>
                <div className="text-[10px] font-semibold">{idx + 1}</div>
                <div className="text-[10px] leading-tight font-bold">{PIPELINE_LABELS[st]}</div>
                {stamp && (
                  <div className={`text-[9px] ${active ? 'text-indigo-100' : 'text-slate-500'}`}>
                    {new Date(stamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          Current: <b className="text-slate-800">{PIPELINE_LABELS[curStatus] || curStatus}</b>
        </div>
      </div>

      {/* ===== ACTIONS ===== */}
      {legalNext.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Next step
          </div>
          <div className="flex flex-wrap gap-2">
            {legalNext.map(st => (
              <button key={st} onClick={() => transition(st)} disabled={busy}
                      data-testid={`audinexa-next-${st}`}
                      className={`px-3 py-1.5 text-xs font-bold rounded ${
                        st === 'CANCELLED'
                          ? 'bg-rose-100 hover:bg-rose-200 text-rose-800'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      } disabled:opacity-50`}>
                → {PIPELINE_LABELS[st]}
              </button>
            ))}
          </div>
          {/* AWB-required hint when the only legal next step is DISPATCHED */}
          {curStatus === 'AWAITING_DISPATCH' && (
            <div
              data-testid="audinexa-dispatch-awb-hint"
              className="mt-2 text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed"
            >
              <b>Note:</b> Marking <b>Dispatched</b> requires an Outbound courier with an
              AWB / tracking number. Book the shipment below first.
            </div>
          )}
        </div>
      )}

      {pipe.is_terminal && (curStatus === 'NOT_PROCEEDING' ? (
        <div className="bg-rose-50 border border-rose-200 rounded p-3 mb-4 text-center text-sm text-rose-800" data-testid="audinexa-not-proceeding-banner">
          <b>Closed – Not Proceeding.</b> The clinic rejected the vendor estimate. You can still log the inbound courier for the unrepaired device.
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-center text-sm text-slate-600">
          Job is in a terminal state. No further transitions allowed.
        </div>
      ))}

      {/* ===== INSPECTION NOTES (shown at RECEIVED) ===== */}
      {curStatus === 'RECEIVED' && (
        <InspectionNotesForm
          ticketNo={ticketNo}
          onDone={() => { load(); onChanged && onChanged(); }}
          existing={t.inspection_notes}
        />
      )}

      {/* ===== INSPECTION SUMMARY (read-only, after RECEIVED) ===== */}
      {curStatus !== 'RECEIVED' && t.inspection_notes && (
        <div className="mb-5 bg-blue-50 border border-blue-200 rounded p-3 text-xs"
             data-testid="audinexa-inspection-summary">
          <div className="text-[10px] uppercase tracking-wider font-bold text-blue-900 mb-1">
            Inspection Notes
          </div>
          <div className="text-slate-700 whitespace-pre-wrap">{t.inspection_notes}</div>
        </div>
      )}

      {/* ===== PHASE 14 — Vendor-route extras (loaner, print service note,
                 mark return un-repaired). Mounts only on VENDOR-route
                 tickets; component itself decides which buttons to show. =====*/}
      {t.repair_location === 'VENDOR' && !pipe.is_terminal && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Patient handover &amp; loaner
          </div>
          <ServiceTicketActions
            ticket={t}
            hasVendorEstimate={pipe.estimates && pipe.estimates.length > 0}
            onChanged={() => { load(); onChanged && onChanged(); }}
          />
        </div>
      )}

      {/* ===== END-OF-PIPELINE: Print Service Completion Report + Auto-Invoice ===== */}
      {(curStatus === 'READY_FOR_PICKUP' ||
        curStatus === 'DELIVERED_TO_CLIENT' ||
        curStatus === 'CLOSED') && (
        <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2"
             data-testid="audinexa-final-report-banner">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-emerald-900">Service complete</div>
              <div className="text-[11px] text-emerald-800">Print the A5 Service Completion Report &amp; raise the GST invoice (18%, SAC 9985).</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setSlipMode('completion')}
                      data-testid="audinexa-print-service-report"
                      className="px-3 py-2 text-xs font-bold bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-900 rounded shadow-sm">
                🖨️ Service Completion Report (A5)
              </button>
              {/* 2026-02-19 · wa.me ping once report is ready */}
              <WhatsAppButton
                mobile={t.patient_mobile}
                size="md"
                testid="audinexa-completion-wa-btn"
                label="WhatsApp Patient"
                message={(() => {
                  const cName = _clinicCtx?.name || 'AUDINEXA Clinic';
                  const name = t.patient_name || 'Patient';
                  const job = t.ticket_no || ticketNo;
                  return `Dear ${name}, your hearing aid service is complete. Job No: ${job}. Please collect at your earliest convenience. — ${cName}`;
                })()}
              />
              {/* 2026-02-19 · Email service completion PDF to patient */}
              <EmailDocButton
                email={t.patient_email}
                size="md"
                testid="audinexa-completion-email-btn"
                onSend={async (to) => {
                  const r = await axios.post(
                    `${API}/ha/service-tickets/${t.ticket_no || ticketNo}/email-pdf`,
                    null, { params: { to } },
                  );
                  return r.data;
                }}
              />
              <ServiceInvoiceButton
                ticketNo={ticketNo}
                ticket={pipe?.ticket}
                pipeline={pipe}
                existingInvoice={pipe?.ticket?.invoice_id ? {
                  invoice_id: pipe.ticket.invoice_id,
                  invoice_no: pipe.ticket.invoice_no,
                } : null}
                onError={setErr}
                onGenerated={() => { load(); onChanged && onChanged(); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ===== COURIER SECTION ===== */}
      <Section title="Couriers" count={pipe.shipments.length}
               action={(!pipe.is_terminal || curStatus === 'NOT_PROCEEDING') && (
                 (curStatus === 'AWAITING_DISPATCH' || curStatus === 'NOT_PROCEEDING' ||
                  curStatus === 'REPAIR_IN_PROGRESS' ||
                  curStatus === 'CLIENT_REJECTED' ||
                  curStatus === 'DISPATCHED' ||
                  curStatus === 'IN_TRANSIT') ? (
                   <button onClick={() => setShowCourier(s => !s)}
                           data-testid="audinexa-book-courier-toggle"
                           className="text-[11px] font-semibold text-indigo-600 hover:underline">
                     + Book shipment
                   </button>
                 ) : (
                   <span className="text-[10px] italic text-slate-400">Not applicable at this stage</span>
                 )
               )}>
        {pipe.shipments.length === 0 ? <Empty label="No shipments yet." /> : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-500">
              <tr><th className="text-left">ID</th><th>Dir</th><th className="text-left">Partner</th><th className="text-left">AWB</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {pipe.shipments.map(s => (
                <tr key={s.shipment_id} className="border-t border-slate-100"
                    data-testid={`audinexa-shipment-${s.shipment_id}`}>
                  <td className="py-1 font-mono text-[10px]">{s.shipment_id}</td>
                  <td className="text-center text-[10px]">{s.direction === 'OUTBOUND' ? '📤' : '📥'}</td>
                  <td>{s.courier_partner}</td>
                  <td className="font-mono text-[10px]">
                    {s.awb_number || <span className="italic text-amber-700">pending</span>}
                    {trackingUrlFor(s) && (
                      <a href={trackingUrlFor(s)} target="_blank" rel="noopener noreferrer"
                         onClick={() => navigator.clipboard?.writeText(s.awb_number).catch(() => {})}
                         title="Open courier tracking page (AWB copied to clipboard)"
                         data-testid={`audinexa-track-${s.shipment_id}`}
                         className="ml-1.5 font-sans font-bold text-indigo-600 hover:underline">Track ↗</a>
                    )}
                  </td>
                  <td className="text-center">
                    <span className={`px-1 py-0.5 rounded text-[10px] font-bold ${s.status === 'PENDING_AWB' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{s.status}</span>
                  </td>
                  <td className="text-right">
                    {s.status === 'PENDING_AWB' && (
                      <button
                        type="button"
                        onClick={() => setStampingShipment(s)}
                        data-testid={`audinexa-stamp-awb-${s.shipment_id}`}
                        className="text-[10px] font-bold text-indigo-600 hover:underline"
                      >Stamp AWB →</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {showCourier && <CourierForm ticketNo={ticketNo} curStatus={curStatus} onDone={() => { setShowCourier(false); load(); }} />}
        {stampingShipment && (
          <StampAwbModal
            shipment={stampingShipment}
            onClose={() => setStampingShipment(null)}
            onDone={() => { setStampingShipment(null); load(); onChanged && onChanged(); }}
          />
        )}
      </Section>

      {/* ===== SERVICE V3 · STEP A — VENDOR REPORT ===== */}
      {(t.repair_location === 'VENDOR' || VENDOR_STAGES.includes(curStatus) || t.vendor_report) && (
        <VendorReportStep ticket={t} canEdit={VENDOR_STAGES.includes(curStatus) && curStatus !== 'CLOSED'}
          onSaved={() => { load(); onChanged && onChanged(); }} />
      )}

      {/* ===== ESTIMATES ===== */}
      <Section title="Vendor Estimates" count={pipe.estimates.length}
               action={(curStatus === 'DELIVERED_TO_COMPANY' || curStatus === 'ESTIMATE_PENDING') && (
                 <button onClick={() => setShowEstimate(s => !s)}
                         data-testid="audinexa-record-estimate-toggle"
                         className="text-[11px] font-semibold text-indigo-600 hover:underline">
                   + Record estimate
                 </button>
               )}>
        {pipe.estimates.length === 0 ? <Empty label="No estimates received yet." /> : (
          <div className="space-y-2">
            {pipe.estimates.map(e => {
              const conveyed = e.conveyed_amount;
              const disc = e.discount;
              const finalAmt = e.warranty_covered ? 0 :
                (conveyed != null ? Math.max(0, Number(conveyed) - Number(disc || 0)) : null);
              return (
                <div key={e.estimate_id}
                     className="border border-slate-200 rounded p-2.5 text-xs space-y-1"
                     data-testid={`audinexa-estimate-${e.estimate_id}`}>
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <div className="font-mono text-[10px] font-bold text-slate-700">{e.estimate_id}</div>
                      <div className="text-[11px] font-semibold mt-0.5">{e.vendor_name || '—'}</div>
                    </div>
                    <div className="text-right">
                      {e.warranty_covered ? (
                        <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded text-[10px] font-bold">WARRANTY</span>
                      ) : (
                        <div>
                          {conveyed != null ? (
                            <>
                              <div className="font-mono font-bold text-slate-800 text-[13px]">{fmtINR(finalAmt)}</div>
                              <div className="text-[9px] text-slate-500">final to patient</div>
                            </>
                          ) : (
                            <div className="font-mono font-bold text-slate-800">{fmtINR(e.amount)}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing breakdown grid */}
                  {!e.warranty_covered && (
                    <div className="bg-slate-50 rounded p-1.5 grid grid-cols-3 gap-1 text-[10px]">
                      <div>
                        <div className="text-slate-500 uppercase font-semibold">Vendor est.</div>
                        <div className="font-mono font-bold">{fmtINR(e.amount)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase font-semibold">Conveyed</div>
                        <div className="font-mono font-bold">{conveyed != null ? fmtINR(conveyed) : '—'}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase font-semibold">Discount</div>
                        <div className="font-mono font-bold text-emerald-700">{disc ? `- ${fmtINR(disc)}` : '—'}</div>
                      </div>
                    </div>
                  )}

                  {/* Conveyed-by / received-on / ETA strip */}
                  <div className="text-[10px] text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5">
                    {e.conveyed_by_name && (
                      <span><span className="text-slate-400">Conveyed by:</span> <b>{e.conveyed_by_name}</b></span>
                    )}
                    {e.conveyed_at && (
                      <span><span className="text-slate-400">on</span> {new Date(e.conveyed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                    {e.eta_days != null && <span><span className="text-slate-400">ETA:</span> {e.eta_days}d</span>}
                    {e.received_on && <span><span className="text-slate-400">Received:</span> {e.received_on}</span>}
                  </div>

                  {e.repair_notes && <div className="text-[11px] text-slate-700 italic border-t border-slate-100 pt-1 mt-1">{e.repair_notes}</div>}
                </div>
              );
            })}
          </div>
        )}
        {showEstimate && <EstimateForm ticketNo={ticketNo} onDone={() => { setShowEstimate(false); load(); }} />}
      </Section>

      {/* ===== APPROVALS ===== */}
      <Section title="Step B · Estimate — Clinic Decision" count={pipe.approvals.length}>
        {pipe.approvals.length === 0 ? <Empty label="No approvals yet." /> : (
          <div className="space-y-2">
            {pipe.approvals.map(a => (
              <ApprovalRow key={a.approval_id} approval={a} onChanged={load} />
            ))}
          </div>
        )}
      </Section>

      {/* ===== SERVICE V3 · STEP C — INBOUND RETURN ===== */}
      {(t.repair_location === 'VENDOR' || pipe.shipments.some(s => s.direction === 'INBOUND')) && (
        <InboundReturnStep ticket={t} shipments={pipe.shipments} onChanged={() => { load(); onChanged && onChanged(); }} />
      )}

      {/* ===== SERVICE V3 · STEP D — SERIAL REASSIGNMENT ===== */}
      {(t.serial_no || t.serial_id || t.instrument_serial || t.replaced_serial_no) && curStatus !== 'CANCELLED' && (
        <SerialReassignStep ticket={t} canReassign={REASSIGN_ROLES.includes(_authCtx.user?.role)}
          onChanged={() => { load(); onChanged && onChanged(); }} />
      )}

      {/* ===== WHATSAPP PREVIEW ===== */}
      {waMessage && (
        <div className="fixed bottom-4 right-4 z-60 max-w-sm bg-white border-2 border-emerald-400 rounded-lg shadow-2xl p-4"
             data-testid="audinexa-whatsapp-preview">
          <div className="flex justify-between mb-2">
            <div className="text-xs font-bold text-emerald-800">WhatsApp · {waMessage.status || 'message'}</div>
            <button onClick={() => setWaMessage(null)} className="text-slate-400 hover:text-slate-700">×</button>
          </div>
          {waMessage.message ? (
            <>
              <div className="bg-emerald-50 text-xs p-2 rounded mb-2">{waMessage.message}</div>
              {waMessage.url ? (
                <a href={waMessage.url} target="_blank" rel="noreferrer"
                   data-testid="audinexa-whatsapp-open"
                   className="block text-center py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded">
                  Open in WhatsApp →
                </a>
              ) : (
                <div className="text-[10px] text-slate-500 italic">No patient mobile on file — copy message manually.</div>
              )}
            </>
          ) : (
            <div className="text-xs italic text-slate-500">
              {waMessage.note || 'No template configured for this status.'}
            </div>
          )}
        </div>
      )}
      {/* ===== CONFLICT RESOLUTION (3-way merge) ===== */}
      <ConflictResolutionModal
        open={!!conflict}
        onClose={() => setConflict(null)}
        base={conflict?.baseDoc}
        local={conflict?.localEdit}
        server={conflict?.serverDoc}
        currentVersion={conflict?.currentVersion || 1}
        expectedVersion={conflict?.expectedVersion || 1}
        recordLabel={`Service Job ${ticketNo}`}
        fields={[
          { key: 'status',            label: 'Status',
            render: (v) => v ? <span className="font-mono">{v}</span> : '—' },
          { key: 'diagnosis',         label: 'Diagnosis' },
          { key: 'inspection_notes',  label: 'Inspection notes' },
          { key: 'resolution_notes',  label: 'Resolution notes' },
          { key: 'cost_to_patient',   label: 'Cost (₹)' },
          { key: 'warranty_covered',  label: 'Warranty' },
          { key: 'technician_name',   label: 'Technician' },
        ]}
        onResolve={resolveConflict}
      />

      {jobCardOpen && (
        <ReportViewerModal
          endpoint={`/ha/service-tickets/${ticketNo}/job-card.pdf`}
          filename={`service-${ticketNo}.pdf`}
          title="Service Report / Job Card"
          subtitle={`${t?.patient_name || 'Patient'} · Ticket ${ticketNo}`}
          onClose={() => setJobCardOpen(false)}
        />
      )}

      {/* Feb 2026 · A5 service slip — Drop-off Ack or Completion Report */}
      {slipMode && (
        <ServiceSlipPrintModal
          ticket={pipe.ticket}
          pipeline={pipe}
          mode={slipMode}
          onClose={() => setSlipMode(null)}
        />
      )}

      {/* Feb 2026 · A5 landscape shipping label for vendor-bound jobs. */}
      {shipLabelOpen && (
        <ShippingLabelModal
          clinicName={shipClinicName}
          branch={shipBranch}
          vendor={shipVendor}
          referenceLabel="Service Job Number"
          referenceNo={t.ticket_no || ticketNo}
          date={t.dispatched_at || t.created_at}
          vendorFixHref="/ha/vendors"
          branchFixHref="/settings"
          onClose={() => setShipLabelOpen(false)}
        />
      )}

    </DrawerShell>
  );
}

/* ============ SUB-COMPONENTS ============ */

function DrawerShell({ children, onClose, title }) {
  useEscapeKey(onClose);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}
           data-testid="audinexa-pipeline-drawer">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold">{title || 'Service Job'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Section({ title, count, action, children }) {
  return (
    <div className="mb-5 border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold">
          {title} <span className="text-slate-400">({count})</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

const Skel = () => <div className="space-y-3 p-4"><div className="h-8 bg-slate-100 rounded animate-pulse" /><div className="h-24 bg-slate-100 rounded animate-pulse" /></div>;
const Empty = ({ label }) => <div className="text-[11px] italic text-slate-400 text-center py-3">{label}</div>;


function ServiceInvoiceButton({ ticketNo, ticket, pipeline, existingInvoice, onError, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const [invoice, setInvoice] = useState(existingInvoice || null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [finalAmount, setFinalAmount] = useState('');
  const [courierCharge, setCourierCharge] = useState('');

  // Sync if pipe.ticket.invoice_id changes (e.g. on reload)
  useEffect(() => { setInvoice(existingInvoice || null); }, [existingInvoice]);

  // Pre-fill: prefer APPROVED estimate → latest estimate → ticket.cost_to_patient.
  // Latest estimate is offered as a manual "prefill" button so an unapproved
  // vendor quote never auto-populates the invoice (data-integrity guard).
  const approvedAmount = useMemo(() => {
    if (!pipeline?.estimates?.length) return null;
    const approvedIds = new Set(
      (pipeline.approvals || [])
        .filter(a => a.decision === 'APPROVED')
        .map(a => a.estimate_id),
    );
    const approved = pipeline.estimates.filter(e => approvedIds.has(e.estimate_id));
    const chosen = approved.length ? approved[approved.length - 1] : null;
    if (!chosen || chosen.warranty_covered) return null;
    const conveyed = Number(chosen.conveyed_amount ?? chosen.amount ?? 0);
    const disc = Number(chosen.discount ?? 0);
    return Math.max(0, conveyed - disc);
  }, [pipeline]);

  const latestConveyed = useMemo(() => {
    if (!pipeline?.estimates?.length) return null;
    const latest = pipeline.estimates[pipeline.estimates.length - 1];
    if (!latest || latest.warranty_covered) return null;
    const conveyed = Number(latest.conveyed_amount ?? latest.amount ?? 0);
    const disc = Number(latest.discount ?? 0);
    return Math.max(0, conveyed - disc);
  }, [pipeline]);

  const isUnderWarranty = ticket?.warranty_status === 'under_warranty' || !!ticket?.warranty_covered;

  const open = () => {
    // Preferred prefill: approved amount → ticket.cost_to_patient (already
    // stamped on prior invoice-raise or manual entry). Latest-estimate is
    // deliberately NOT auto-filled — front-desk must accept it explicitly.
    const seed = approvedAmount != null
      ? approvedAmount
      : (Number(ticket?.cost_to_patient) > 0 ? Number(ticket.cost_to_patient) : null);
    setFinalAmount(seed != null ? String(seed) : '');
    setCourierCharge('');
    setShowPrompt(true);
  };

  const generate = async () => {
    if (!isUnderWarranty) {
      const num = Number(finalAmount);
      if (finalAmount === '' || Number.isNaN(num) || num <= 0) {
        onError && onError('Enter the final service amount (₹) — must be greater than zero.');
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        final_amount: isUnderWarranty ? 0 : Number(finalAmount),
        courier_charge: Number(courierCharge) || 0,
        warranty_covered: isUnderWarranty,
      };
      const r = await axios.post(
        `${API}/ha/service-tickets/${ticketNo}/invoice`, body,
      );
      setInvoice({ invoice_id: r.data.invoice_id, invoice_no: r.data.invoice_no });
      setShowPrompt(false);
      onGenerated && onGenerated();
    } catch (e) {
      onError && onError(describeError(e, 'Failed to raise invoice'));
    } finally { setBusy(false); }
  };

  if (invoice && invoice.invoice_id) {
    return (
      <a href={`/billing/invoice/${invoice.invoice_id}`}
         data-testid="audinexa-view-invoice"
         className="px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded shadow inline-flex items-center gap-1.5">
        ✓ Invoice {invoice.invoice_no || ''}
      </a>
    );
  }
  return (
    <>
      <button onClick={open} disabled={busy}
              data-testid="audinexa-generate-invoice"
              className="px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded shadow inline-flex items-center gap-1.5">
        {busy ? 'Raising…' : '💰 Raise Invoice (18% GST)'}
      </button>
      {showPrompt && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
             onClick={() => !busy && setShowPrompt(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5"
               onClick={(e) => e.stopPropagation()}
               data-testid="audinexa-invoice-prompt">
            <h3 className="text-base font-bold mb-1 text-slate-900">Raise GST Invoice · {ticketNo}</h3>
            <p className="text-[11px] text-slate-500 mb-3">
              Confirm the final amount charged to the patient. 18% GST (SAC 9985) is added automatically and treated as inclusive on the tax invoice.
            </p>
            {isUnderWarranty ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-900 mb-3"
                   data-testid="audinexa-invoice-warranty-lock">
                <b>Under Warranty</b> — service line locked at <b>₹0</b>. Add courier charges below if applicable.
              </div>
            ) : (
              <div className="mb-3">
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">
                  Final Service Amount (₹) — as conveyed to patient *
                </label>
                <input
                  type="number" min="0" step="0.01"
                  value={finalAmount}
                  onChange={(e) => setFinalAmount(e.target.value)}
                  data-testid="audinexa-invoice-amount"
                  placeholder="e.g. 25000"
                  autoFocus
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
                />
                <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                  {approvedAmount != null && (
                    <button type="button"
                            data-testid="audinexa-invoice-prefill-approved"
                            onClick={() => setFinalAmount(String(approvedAmount))}
                            className="px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800 rounded">
                      Approved: ₹{approvedAmount.toLocaleString('en-IN')}
                    </button>
                  )}
                  {latestConveyed != null && latestConveyed !== approvedAmount && (
                    <button type="button"
                            data-testid="audinexa-invoice-prefill-latest"
                            onClick={() => setFinalAmount(String(latestConveyed))}
                            className="px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 rounded">
                      Latest estimate: ₹{latestConveyed.toLocaleString('en-IN')}
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-1 italic">
                  Amount is GST-inclusive — patient pays this exact figure.
                </div>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">
                Courier Charges (₹) · optional
              </label>
              <input
                type="number" min="0" step="0.01"
                value={courierCharge}
                onChange={(e) => setCourierCharge(e.target.value)}
                data-testid="audinexa-invoice-courier"
                placeholder="e.g. 200 (leave blank if none)"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
              />
              <div className="text-[10px] text-slate-500 mt-0.5 italic">
                Added as a separate taxable line on the invoice (18% GST).
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowPrompt(false)} disabled={busy}
                      data-testid="audinexa-invoice-cancel"
                      className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded">
                Cancel
              </button>
              <button onClick={generate} disabled={busy}
                      data-testid="audinexa-invoice-confirm"
                      className="px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded shadow-sm">
                {busy ? 'Raising…' : '💰 Raise Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function InspectionNotesForm({ ticketNo, onDone, existing }) {
  const [note, setNote] = useState(existing || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (toStatus) => {
    if (toStatus === 'INSPECTED' && (!note || note.trim().length < 5)) {
      setErr('Add inspection notes (at least 5 characters) before marking inspected.');
      return;
    }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/ha/service-tickets/${ticketNo}/transition`, {
        to_status: toStatus, note: note.trim() || undefined,
      });
      onDone();
    } catch (e) { setErr(describeError(e, 'Failed to save inspection notes')); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-5 bg-blue-50 border border-blue-200 rounded p-3"
         data-testid="audinexa-inspection-form">
      <div className="text-[10px] uppercase tracking-wider font-bold text-blue-900 mb-2">
        Inspection notes
      </div>
      {err && <ErrorToast err={err} testid="audinexa-inspection-err" />}
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="What did you find? (e.g., receiver crackling, mic dead, water damage to shell, no power on battery swap…)"
        data-testid="audinexa-inspection-input"
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs"
      />
      <div className="flex gap-2 mt-2">
        <button onClick={() => submit('INSPECTED')} disabled={busy}
                data-testid="audinexa-inspection-save"
                className="px-3 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded">
          Save &amp; mark Inspected →
        </button>
        <span className="text-[10px] text-slate-500 self-center italic">
          You can also pick a state directly above; notes will be attached to this job.
        </span>
      </div>
    </div>
  );
}


// Public tracking pages per predefined courier; custom couriers use their saved URL.
const COURIER_TRACKING = {
  bluedart: (awb) => `https://www.bluedart.com/web/guest/trackdartresultthirdparty?trackFor=0&trackNo=${awb}`,
  dtdc: (awb) => `https://txk.dtdc.com/ctbs-tracking/customerInterface.tr?submitName=showCITrackingDetails&cType=Consignment&cnNo=${awb}`,
  delhivery: (awb) => `https://www.delhivery.com/track-v2/package/${awb}`,
  shiprocket: (awb) => `https://shiprocket.co/tracking/${awb}`,
};

function trackingUrlFor(s) {
  if (!s.awb_number) return null;
  const awb = encodeURIComponent(s.awb_number);
  const builtIn = COURIER_TRACKING[(s.courier_partner || '').toLowerCase()];
  if (builtIn) return builtIn(awb);
  return s.tracking_url ? s.tracking_url.replace('{awb}', awb) : null;
}

function CourierForm({ ticketNo, curStatus, onDone }) {
  const [partner, setPartner] = useState('Bluedart');
  const [customPartner, setCustomPartner] = useState('');
  const [customTrackUrl, setCustomTrackUrl] = useState('');
  const [awb, setAwb] = useState('');
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().slice(0, 10));
  const [etaDate, setEtaDate] = useState('');
  const [toAddr, setToAddr] = useState('');
  const [direction, setDirection] = useState(['RETURN_SHIPPED', 'CLIENT_APPROVED', 'NOT_PROCEEDING', 'REPAIR_IN_PROGRESS', 'CLIENT_REJECTED'].includes(curStatus) ? 'INBOUND' : 'OUTBOUND');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);

  const willAutoAdvance =
    (direction === 'OUTBOUND' && curStatus === 'AWAITING_DISPATCH') ||
    (direction === 'INBOUND' && (curStatus === 'REPAIR_IN_PROGRESS' || curStatus === 'CLIENT_REJECTED'));
  const autoNext = willAutoAdvance
    ? (direction === 'OUTBOUND' ? 'Dispatched' : 'Return Shipped')
    : null;

  const submit = async () => {
    setErr(''); setSuccess(null);
    if (partner === 'Custom' && !customPartner.trim()) { setErr('Enter the courier name'); return; }
    if (partner === 'Custom' && customTrackUrl.trim() && !/^https?:\/\/\S+$/.test(customTrackUrl.trim())) {
      setErr('Tracking URL must start with http:// or https://'); return;
    }
    if (!awb || awb.trim().length < 4) { setErr('AWB number is required (min 4 chars)'); return; }
    setBusy(true);
      try {
        const body = {
          ticket_no: ticketNo, direction,
          courier_partner: partner === 'Custom' ? customPartner.trim() : partner,
          awb_number: awb.trim(), dispatch_date: dispatchDate || undefined,
          to_address: toAddr || undefined,
        };
        if (etaDate) body.eta_date = etaDate;
        if (partner === 'Custom' && customTrackUrl.trim()) body.tracking_url = customTrackUrl.trim();
        const r = await axios.post(`${API}/ha/couriers`, body);
        setSuccess({ shipment_id: r.data.shipment_id, advanced: !!autoNext, advancedTo: autoNext });
        // Brief delay to let the user see the confirmation, then close + reload
        setTimeout(() => onDone(), 900);
      } catch (e) {
        setErr(describeError(e, 'Failed to book shipment'));
      } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-2"
         data-testid="audinexa-courier-form">
      {err && <ErrorToast err={err} testid="audinexa-courier-err" />}
      {success && (
        <div className="bg-emerald-100 text-emerald-900 p-2 rounded text-[11px] font-bold"
             data-testid="audinexa-courier-success">
          ✓ Shipment {success.shipment_id} booked.
          {success.advanced && <> Pipeline auto-advanced to <b>{success.advancedTo}</b>.</>}
        </div>
      )}
      {autoNext && !success && (
        <div className="bg-amber-50 text-amber-800 p-1.5 rounded text-[10px] italic" data-testid="audinexa-courier-hint">
          Booking this shipment will move the job to <b>{autoNext}</b>.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <select value={direction} onChange={(e) => setDirection(e.target.value)}
                data-testid="audinexa-courier-direction"
                className="border border-slate-300 rounded px-2 py-1 text-xs">
          <option value="OUTBOUND">Outbound (to company)</option>
          <option value="INBOUND">Inbound (from company)</option>
        </select>
        <select value={partner} onChange={(e) => setPartner(e.target.value)}
                data-testid="audinexa-courier-partner"
                className="border border-slate-300 rounded px-2 py-1 text-xs">
          {['Bluedart', 'DTDC', 'Delhivery', 'Shiprocket', 'Custom'].map(p =>
            <option key={p} value={p}>{p}</option>,
          )}
        </select>
      </div>
      {partner === 'Custom' && (
        <input value={customPartner} onChange={(e) => setCustomPartner(e.target.value)}
               placeholder="Enter courier name (e.g. ST Courier)" maxLength={60} required
               data-testid="audinexa-courier-custom-name"
               className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
      )}
      {partner === 'Custom' && (
        <input value={customTrackUrl} onChange={(e) => setCustomTrackUrl(e.target.value)}
               placeholder="Tracking URL (optional) — use {awb} where the AWB goes" maxLength={500}
               data-testid="audinexa-courier-custom-track-url"
               className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
      )}
      <input value={awb} onChange={(e) => setAwb(e.target.value)}
             placeholder="AWB Number"
             data-testid="audinexa-courier-awb"
             className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono" />
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)}
               className="border border-slate-300 rounded px-2 py-1 text-xs" />
        <input type="date" value={etaDate} onChange={(e) => setEtaDate(e.target.value)}
               className="border border-slate-300 rounded px-2 py-1 text-xs" placeholder="ETA" />
      </div>
      <input value={toAddr} onChange={(e) => setToAddr(e.target.value)}
             placeholder="Destination address" className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
      <button onClick={submit} disabled={busy || !awb || (partner === 'Custom' && !customPartner.trim())}
              data-testid="audinexa-courier-submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold py-1.5 rounded text-xs">
        Book shipment
      </button>
    </div>
  );
}

function EstimateForm({ ticketNo, onDone }) {
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState('');
  const [conveyedAmount, setConveyedAmount] = useState('');
  const [discount, setDiscount] = useState('');
  const [warranty, setWarranty] = useState(false);
  const [eta, setEta] = useState(4);
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Live patient-facing total preview
  const conveyedNum = Number(conveyedAmount) || 0;
  const discountNum = Number(discount) || 0;
  const finalToPatient = warranty ? 0 : Math.max(0, conveyedNum - discountNum);

  const submit = async () => {
    setErr('');
    if (!vendor || vendor.trim().length < 2) { setErr('Vendor / service centre name is required'); return; }
    if (!warranty && (!amount || Number(amount) <= 0)) { setErr('Vendor amount must be greater than zero (or tick Warranty covered)'); return; }
    setBusy(true);
    try {
      const body = {
        ticket_no: ticketNo,
        vendor_name: vendor.trim(),
        amount: Number(amount) || 0,
        warranty_covered: warranty,
        eta_days: Number(eta) || null,
        repair_notes: notes || null,
      };
      if (conveyedAmount !== '' && !Number.isNaN(conveyedNum)) body.conveyed_amount = conveyedNum;
      if (discount !== '' && !Number.isNaN(discountNum)) body.discount = discountNum;
      await axios.post(`${API}/ha/service-estimates`, body);
      onDone();
    } catch (e) {
      setErr(describeError(e, 'Failed to record estimate'));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-3 text-xs space-y-2"
         data-testid="audinexa-estimate-form">
      {err && <ErrorToast err={err} testid="audinexa-estimate-err" />}
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-0.5">Vendor / service centre *</label>
        <input value={vendor} onChange={(e) => setVendor(e.target.value)}
               placeholder="e.g. Phonak Service Mumbai"
               data-testid="audinexa-estimate-vendor"
               className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-0.5">Estimated amount (vendor) *</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                 disabled={warranty} placeholder="₹"
                 data-testid="audinexa-estimate-amount"
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs disabled:bg-slate-100" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-0.5">Conveyed amount (to patient)</label>
          <input type="number" min="0" step="0.01" value={conveyedAmount} onChange={(e) => setConveyedAmount(e.target.value)}
                 disabled={warranty} placeholder="₹ (defaults to vendor amt)"
                 data-testid="audinexa-estimate-conveyed"
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs disabled:bg-slate-100" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-0.5">Discount (₹)</label>
          <input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)}
                 disabled={warranty} placeholder="0"
                 data-testid="audinexa-estimate-discount"
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs disabled:bg-slate-100" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-0.5">ETA (days)</label>
          <input type="number" value={eta} onChange={(e) => setEta(e.target.value)}
                 placeholder="4" min={0}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
        </div>
      </div>

      <label className="inline-flex items-center gap-1 text-[11px]">
        <input type="checkbox" checked={warranty} onChange={(e) => setWarranty(e.target.checked)}
               data-testid="audinexa-estimate-warranty" />
        Warranty covered (₹0 to patient)
      </label>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-0.5">Repair notes</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="What's the vendor proposing? Parts to be replaced, sound check details…"
                  className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
      </div>

      {/* Live preview */}
      {!warranty && (conveyedNum > 0 || discountNum > 0) && (
        <div className="bg-white border border-amber-300 rounded p-2 text-[11px]"
             data-testid="audinexa-estimate-preview">
          <div className="flex justify-between"><span>Conveyed</span><span className="font-mono">{fmtINR(conveyedNum)}</span></div>
          {discountNum > 0 && <div className="flex justify-between text-emerald-700"><span>Less discount</span><span className="font-mono">- {fmtINR(discountNum)}</span></div>}
          <div className="flex justify-between border-t border-amber-200 pt-1 mt-1 font-bold">
            <span>Final to patient</span><span className="font-mono">{fmtINR(finalToPatient)}</span>
          </div>
        </div>
      )}

      <button onClick={submit} disabled={busy}
              data-testid="audinexa-estimate-submit"
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white font-bold py-1.5 rounded text-xs">
        {busy ? 'Saving…' : 'Record estimate + request patient approval'}
      </button>
    </div>
  );
}

function ApprovalRow({ approval, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [contact, setContact] = useState('');
  const [err, setErr] = useState('');

  const decide = async (decision) => {
    const msg = decision === 'REJECTED'
      ? 'Reject this estimate? The job will be Closed – Not Proceeding and the device returned unrepaired.'
      : 'Approve this estimate? The repair will go ahead.';
    if (!window.confirm(msg)) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/ha/customer-approvals/${approval.approval_id}/decide`, {
        decision,
        contact_number: contact.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onChanged();
    } catch (e) {
      setErr(describeError(e, 'Failed to save decision'));
    } finally { setBusy(false); }
  };
  const colors = {
    PENDING: 'bg-amber-100 text-amber-900',
    APPROVED: 'bg-emerald-100 text-emerald-900',
    REJECTED: 'bg-rose-100 text-rose-900',
  };
  return (
    <div className={`rounded p-2.5 text-xs ${colors[approval.decision]}`}
         data-testid={`audinexa-approval-${approval.approval_id}`}>
      <div className="flex justify-between items-center mb-1">
        <div className="font-mono text-[10px] font-bold">{approval.approval_id}</div>
        <div className="text-[10px] font-bold">{approval.decision}</div>
      </div>
      {err && <div className="mb-1"><ErrorToast err={err} testid={`audinexa-approval-err-${approval.approval_id}`} /></div>}
      {approval.decision === 'PENDING' ? (
        <div className="space-y-1.5 mt-2">
          <div>
            <label className="block text-[10px] uppercase font-bold mb-0.5">Contact number reached</label>
            <input value={contact} onChange={(e) => setContact(e.target.value)}
                   placeholder="e.g. patient mobile / attendant number"
                   data-testid={`audinexa-approval-contact-${approval.approval_id}`}
                   className="w-full border border-slate-300 rounded px-2 py-1 text-[11px] bg-white" />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold mb-0.5">Notes (rejection reason / approval context)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="What did the patient say?"
                   data-testid={`audinexa-approval-notes-${approval.approval_id}`}
                   className="w-full border border-slate-300 rounded px-2 py-1 text-[11px] bg-white" />
          </div>
          <div className="flex gap-1">
            <button onClick={() => decide('APPROVED')} disabled={busy}
                    data-testid={`audinexa-approve-${approval.approval_id}`}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-bold py-1 rounded text-[11px]">
              ✓ Approved by clinic
            </button>
            <button onClick={() => decide('REJECTED')} disabled={busy}
                    data-testid={`audinexa-reject-${approval.approval_id}`}
                    className="flex-1 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white font-bold py-1 rounded text-[11px]">
              ✗ Rejected — Not Proceeding
            </button>
          </div>
        </div>
      ) : (
        <div className="text-[11px] mt-1 space-y-0.5">
          <div>
            <span className="text-[10px] uppercase font-bold">{approval.decision === 'APPROVED' ? 'Approved by' : 'Rejected by'}:</span>{' '}
            <span className="font-bold">{approval.decided_by_name || '—'}</span>
          </div>
          {approval.decided_at && (
            <div className="text-[10px] opacity-70">on {new Date(approval.decided_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          )}
          {approval.contact_number && (
            <div className="text-[10px]"><span className="opacity-70">Contact reached:</span> <span className="font-mono">{approval.contact_number}</span></div>
          )}
          {approval.notes && <div className="italic mt-0.5">"{approval.notes}"</div>}
        </div>
      )}
    </div>
  );
}



/**
 * Stamp AWB modal — wired to PATCH /api/ha/couriers/{id}/awb.
 * Opens from a PENDING_AWB row in the courier table (Phase 14).
 */
function StampAwbModal({ shipment, onClose, onDone }) {
  useEscapeKey(onClose);
  const [awb, setAwb] = useState('');
  const [partner, setPartner] = useState(shipment.courier_partner === '(pending)' ? '' : (shipment.courier_partner || ''));
  const [dispatchDate, setDispatchDate] = useState('');
  const [etaDate, setEtaDate] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!awb.trim()) { setErr('AWB / tracking number is required'); return; }
    setBusy(true); setErr('');
    try {
      await axios.patch(`${API}/ha/couriers/${shipment.shipment_id}/awb`, {
        awb_number: awb.trim(),
        courier_partner: partner.trim() || undefined,
        dispatch_date: dispatchDate || undefined,
        eta_date: etaDate || undefined,
      });
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Stamp failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
        >
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5"
           data-testid="audinexa-stamp-awb-modal">
        <h3 className="text-base font-bold mb-1">Stamp AWB</h3>
        <p className="text-[11px] text-slate-500 mb-3 font-mono">
          {shipment.shipment_id} · {shipment.direction}
        </p>
        {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-3">{err}</div>}
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">AWB / Tracking *</label>
        <input
          value={awb} onChange={(e) => setAwb(e.target.value)}
          data-testid="audinexa-stamp-awb-input"
          placeholder="e.g. BL123456789IN"
          className="w-full border border-slate-300 rounded px-2 py-1 text-sm mb-3 font-mono"
        />
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Courier Partner</label>
        <input
          value={partner} onChange={(e) => setPartner(e.target.value)}
          data-testid="audinexa-stamp-partner"
          placeholder="DTDC / Bluedart / India Post…"
          className="w-full border border-slate-300 rounded px-2 py-1 text-sm mb-3"
        />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Dispatch date</label>
            <input
              type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)}
              data-testid="audinexa-stamp-dispatch"
              className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">ETA</label>
            <input
              type="date" value={etaDate} onChange={(e) => setEtaDate(e.target.value)}
              data-testid="audinexa-stamp-eta"
              className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
          <button onClick={submit} disabled={busy}
                  data-testid="audinexa-stamp-awb-submit"
                  className="px-4 py-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded">
            {busy ? 'Stamping…' : 'Stamp AWB'}
          </button>
        </div>
      </div>
    </div>
  );
}
